import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { homeLabel } from "@/lib/home";
import { toast } from "sonner";
import { Phone, PhoneCall, PhoneOff, Mic, Loader2, BadgeCheck, UserRound, Wrench, MessageSquareText, AlertTriangle, Bot, User } from "lucide-react";
import { V } from "@/lib/vocab";
import { scrollToEnd } from "@/lib/scroll";

// Live browser voice demo — the real concierge brain (community docs + tools) over WebRTC.
// No phone number needed. Super-admin only (endpoints are gated).
export default function VoiceCallPanel({ tenantId }) {
  const [tenant, setTenant] = useState(null);
  const [households, setHouseholds] = useState([]);
  const [persona, setPersona] = useState("resident");
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState("idle"); // idle | connecting | live | ended | error
  const [transcript, setTranscript] = useState([]);
  const [toolLogs, setToolLogs] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");

  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const micRef = useRef(null);
  const audioRef = useRef(null);
  const scrollRef = useRef(null);
  const personaRef = useRef({ persona, phone });

  useEffect(() => {
    if (!tenantId) return;
    setTenant(null); setHouseholds([]); endCall();
    api.get(`/tenants/${tenantId}`).then((r) => setTenant(r.data)).catch(() => {});
    api.get(`/tenants/${tenantId}/households`).then((r) => {
      setHouseholds(r.data);
      const known = r.data.find((h) => h.phone);
      setPhone(known?.phone || "+15125550012");
    }).catch(() => {});
    return () => endCall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  useEffect(() => { scrollToEnd(scrollRef.current); }, [transcript]);
  useEffect(() => { personaRef.current = { persona, phone }; }, [persona, phone]);

  const residents = households.filter((h) => h.phone).slice(0, 5).map((h) => ({
    phone: h.phone,
    name: h.people?.[0]?.name?.split(" ")[0] || `${V.one("person")}`,
    home: homeLabel(h),
    verified: h.verified !== false,
  }));
  const unknownPhone = "+15129998888";

  const pushAssistantDelta = (delta) => {
    setTranscript((t) => {
      const last = t[t.length - 1];
      if (last && last.role === "assistant" && last.streaming) {
        const copy = [...t]; copy[copy.length - 1] = { ...last, text: last.text + delta }; return copy;
      }
      return [...t, { role: "assistant", text: delta, streaming: true }];
    });
  };
  const finalizeAssistant = () => setTranscript((t) => t.map((m) => (m.streaming ? { ...m, streaming: false } : m)));
  const pushUser = (text) => { if (text?.trim()) setTranscript((t) => [...t, { role: "user", text: text.trim() }]); };

  const runTool = async (name, callId, argsRaw) => {
    let args = {}; try { args = JSON.parse(argsRaw || "{}"); } catch { /* ignore */ }
    const p = personaRef.current.persona === "resident" ? personaRef.current.phone : unknownPhone;
    let result = { ok: false };
    try {
      const r = await api.post(`/voice-demo/${tenantId}/tool`, { name, arguments: args, phone: p });
      result = r.data.result || { ok: true };
      if (r.data.logged) {
        setToolLogs((l) => [...l, { name, ...r.data.logged }]);
        toast.success(r.data.logged.kind === "request" ? `Logged: ${r.data.logged.summary}` : "Message taken for the team");
      }
    } catch { result = { ok: false }; }
    const dc = dcRef.current;
    if (dc && dc.readyState === "open") {
      dc.send(JSON.stringify({ type: "conversation.item.create", item: { type: "function_call_output", call_id: callId, output: JSON.stringify(result) } }));
      dc.send(JSON.stringify({ type: "response.create" }));
    }
  };

  const handleEvent = (ev) => {
    const t = ev.type || "";
    if (t === "response.output_audio_transcript.delta" || t === "response.audio_transcript.delta") pushAssistantDelta(ev.delta || "");
    else if (t === "response.output_audio_transcript.done" || t === "response.audio_transcript.done" || t === "response.done") finalizeAssistant();
    else if (t === "conversation.item.input_audio_transcription.completed") pushUser(ev.transcript || "");
    else if (t === "response.function_call_arguments.done") runTool(ev.name, ev.call_id, ev.arguments);
    else if (t === "error") { setErrorMsg(ev.error?.message || "Voice error"); }
  };

  const startCall = async () => {
    if (status === "connecting" || status === "live") return;
    setErrorMsg(""); setTranscript([]); setToolLogs([]); setStatus("connecting");
    try {
      const p = persona === "resident" ? phone : unknownPhone;
      const res = residents.find((r) => r.phone === p);
      const { data } = await api.post(`/voice-demo/${tenantId}/session`, {
        persona, phone: p, resident_name: res?.name || "", unit: res?.unit || "",
      });
      const clientSecret = data.client_secret, model = data.model;

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micRef.current = stream;
      const pc = new RTCPeerConnection();
      pcRef.current = pc;
      pc.ontrack = (e) => { if (audioRef.current) audioRef.current.srcObject = e.streams[0]; };
      pc.onconnectionstatechange = () => {
        if (["failed", "disconnected", "closed"].includes(pc.connectionState) && status === "live") endCall();
      };
      stream.getTracks().forEach((tr) => pc.addTrack(tr, stream));

      const dc = pc.createDataChannel("oai-events");
      dcRef.current = dc;
      dc.onopen = () => { setStatus("live"); dc.send(JSON.stringify({ type: "response.create" })); };
      dc.onmessage = (e) => { try { handleEvent(JSON.parse(e.data)); } catch { /* ignore */ } };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const sdpResp = await fetch(`https://api.openai.com/v1/realtime/calls?model=${encodeURIComponent(model)}`, {
        method: "POST", body: offer.sdp,
        headers: { Authorization: `Bearer ${clientSecret}`, "Content-Type": "application/sdp" },
      });
      if (!sdpResp.ok) throw new Error("Could not connect the audio channel");
      const answerSdp = await sdpResp.text();
      await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
    } catch (e) {
      const msg = e?.name === "NotAllowedError" ? "Microphone access was blocked — allow it and try again."
        : (e?.response?.data?.detail || e?.message || "Could not start the call");
      setErrorMsg(msg); setStatus("error"); endCall(true);
    }
  };

  const endCall = (keepStatus = false) => {
    try { dcRef.current?.close(); } catch {}
    try { pcRef.current?.getSenders?.().forEach((s) => s.track?.stop()); pcRef.current?.close(); } catch {}
    try { micRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    if (audioRef.current) audioRef.current.srcObject = null;
    dcRef.current = null; pcRef.current = null; micRef.current = null;
    if (!keepStatus) setStatus((s) => (s === "live" || s === "connecting" ? "ended" : s));
  };

  const live = status === "live", connecting = status === "connecting";

  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-8 items-start" data-testid="voice-call-panel">
      <audio ref={audioRef} autoPlay className="hidden" />
      {/* Call device */}
      <div>
        <div className="w-full max-w-[360px] mx-auto rounded-[2.75rem] bg-ink p-3 shadow-2xl shadow-ink/30">
          <div className="rounded-[2.25rem] bg-gradient-to-b from-ink to-ink-800 overflow-hidden flex flex-col h-[620px] text-white">
            <div className="px-6 pt-10 pb-6 text-center border-b border-white/10">
              <div className={`mx-auto h-20 w-20 rounded-full flex items-center justify-center mb-4 ${live ? "bg-emerald-500/20" : "bg-white/10"}`}>
                <div className={`h-16 w-16 rounded-full flex items-center justify-center ${live ? "bg-emerald-500 animate-pulse" : "bg-redline-500"}`}>
                  {connecting ? <Loader2 className="h-7 w-7 animate-spin" /> : live ? <Mic className="h-7 w-7" /> : <Phone className="h-7 w-7" />}
                </div>
              </div>
              <div className="font-heading text-lg font-bold">{tenant?.persona_name || tenant?.name || "Concierge"}</div>
              <div data-testid="voice-status" className="text-sm text-white/60 mt-1">
                {connecting ? "Connecting…" : live ? "Live — speak now" : status === "ended" ? "Call ended" : status === "error" ? "Couldn't connect" : "Ready to call"}
              </div>
            </div>

            <div ref={scrollRef} data-testid="voice-transcript" className="flex-1 overflow-y-auto chat-scroll p-4 space-y-3">
              {transcript.length === 0 && (
                <div className="text-center text-sm text-white/40 mt-10 px-6">
                  {live ? "Say hello — the concierge is listening." : "Start the call, then talk to the concierge with your mic. A live transcript appears here."}
                </div>
              )}
              {transcript.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-3.5 py-2 text-sm leading-snug rounded-2xl flex items-start gap-2 ${m.role === "user" ? "bg-redline-500 text-white rounded-tr-md" : "bg-white/10 text-white rounded-tl-md"}`}>
                    {m.role === "assistant" ? <Bot className="h-4 w-4 mt-0.5 shrink-0 text-emerald-300" /> : <User className="h-4 w-4 mt-0.5 shrink-0 text-white/80" />}
                    <span>{m.text}{m.streaming && <span className="inline-block w-1.5 h-3.5 bg-white/50 ml-0.5 animate-pulse align-middle" />}</span>
                  </div>
                </div>
              ))}
              {toolLogs.map((l, i) => (
                <div key={`t${i}`} className="flex justify-center">
                  <span className="text-[11px] bg-emerald-500/15 text-emerald-300 rounded-full px-3 py-1 flex items-center gap-1.5">
                    {l.kind === "request" ? <Wrench className="h-3 w-3" /> : <MessageSquareText className="h-3 w-3" />} {l.kind === "request" ? `Ticket logged: ${l.summary}` : "Message taken"}
                  </span>
                </div>
              ))}
            </div>

            <div className="p-4 border-t border-white/10">
              {!live && !connecting ? (
                <button data-testid="voice-start-call" onClick={startCall}
                  className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-3 flex items-center justify-center gap-2 transition-colors">
                  <PhoneCall className="h-5 w-5" /> Start call
                </button>
              ) : (
                <button data-testid="voice-end-call" onClick={() => endCall()} disabled={connecting}
                  className="w-full rounded-full bg-redline-500 hover:bg-redline-600 text-white font-semibold py-3 flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                  <PhoneOff className="h-5 w-5" /> {connecting ? "Connecting…" : "End call"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="space-y-6">
        {errorMsg && (
          <div data-testid="voice-error" className="rounded-2xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3 text-sm text-amber-900">
            <AlertTriangle className="h-5 w-5 shrink-0" /> {errorMsg}
          </div>
        )}
        <div className="bg-white rounded-2xl border border-sand-200 p-5">
          <div className="text-sm font-semibold mb-1">Who's calling?</div>
          <p className="text-xs text-muted-foreground mb-3">Call as a verified {V.lc("person")} (personalized greeting, tickets logged to their unit) or an unknown caller (sign-up flow).</p>
          <div className="flex flex-wrap gap-2 mb-3">
            <button data-testid="voice-persona-unknown" onClick={() => { if (!live) setPersona("unknown"); }} disabled={live}
              className={`rounded-full pl-3 pr-4 py-2 text-sm border flex items-center gap-1.5 transition-colors disabled:opacity-50 ${persona === "unknown" ? "bg-ink text-white border-ink" : "bg-amber-50 border-amber-200 text-amber-800 hover:border-amber-400"}`}>
              <UserRound className="h-3.5 w-3.5" /> Unknown caller
            </button>
          </div>
          {persona === "resident" && (
            <div className="flex flex-wrap gap-2">
              {residents.map((r) => (
                <button key={r.phone} data-testid={`voice-resident-${r.phone}`} onClick={() => { if (!live) setPhone(r.phone); }} disabled={live}
                  className={`rounded-full pl-3 pr-4 py-2 text-sm border flex items-center gap-1.5 transition-colors disabled:opacity-50 ${phone === r.phone ? "bg-ink text-white border-ink" : "bg-sand-50 border-sand-200 hover:border-ink"}`}>
                  <BadgeCheck className={`h-3.5 w-3.5 ${phone === r.phone ? "text-emerald-300" : "text-emerald-600"}`} /> {r.name}{r.home ? ` · ${r.home}` : ""}
                </button>
              ))}
            </div>
          )}
          <div className="mt-3 flex gap-2">
            <button onClick={() => { if (!live) setPersona("resident"); }} disabled={live}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors disabled:opacity-50 ${persona === "resident" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-white border-sand-200 text-ink-500 hover:border-ink"}`}>
              Verified {V.lc("person")}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-sand-200 p-5">
          <div className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Mic className="h-4 w-4 text-redline-500" /> How it works</div>
          <ul className="text-xs text-muted-foreground space-y-1.5 leading-relaxed list-disc pl-4">
            <li>This is the <span className="font-semibold text-ink">real concierge</span> — grounded in this {V.lc("org")}'s documents, with the same voice & personality as a live phone call.</li>
            <li>It can <span className="font-semibold text-ink">log a maintenance request</span> or <span className="font-semibold text-ink">take a message</span> — those appear in the {V.lc("org")}'s Requests just like a real call.</li>
            <li className="text-amber-700"><span className="font-semibold">Heads up:</span> a live call uses real OpenAI voice minutes on this {V.lc("org")}'s key.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
